/**
 * Billing Query Hooks
 *
 * React Query hooks for subscription status and the checkout / portal flows.
 * The mutations redirect the browser to the Stripe-hosted URL on success.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { queryKeys } from './keys';
import {
  billingService,
  type BillingStatus,
  type PlanInterval,
  type PlanTier,
} from '@/services/billing';

type BillingRefetchInterval =
  | number
  | false
  | ((query: { state: { data?: BillingStatus } }) => number | false);

/** Current subscription status for the signed-in user. */
export function useBillingStatus(
  enabled = true,
  options?: { refetchInterval?: BillingRefetchInterval }
) {
  return useQuery({
    queryKey: queryKeys.billing.status(),
    queryFn: () => billingService.getBillingStatus(),
    enabled,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: options?.refetchInterval as never,
  });
}

/**
 * The tier catalogue. Long staleTime — prices change on the order of quarters,
 * and this is the same config the backend enforces, so it cannot drift.
 */
export function usePlans() {
  return useQuery({
    queryKey: queryKeys.billing.plans(),
    queryFn: () => billingService.getPlans(),
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Current entitlements and usage. Short staleTime because the numbers move as
 * the family creates stories — a stale meter that says "3 of 8" after the 8th
 * story is worse than a refetch.
 */
export function useEntitlements(enabled = true) {
  return useQuery({
    queryKey: queryKeys.billing.entitlements(),
    queryFn: () => billingService.getEntitlements(),
    enabled,
    staleTime: 30 * 1000,
  });
}

/** Variables for {@link useStartCheckout}: which tier, billed how, plus an
 * optional school / free-pass code. */
export interface StartCheckoutVars {
  tier: PlanTier;
  interval: PlanInterval;
  promoCode?: string;
}

/** Start checkout and redirect to Stripe's hosted page. */
export function useStartCheckout() {
  return useMutation({
    mutationFn: ({ tier, interval, promoCode }: StartCheckoutVars) =>
      billingService.createCheckout(tier, interval, promoCode),
    onSuccess: (url) => {
      if (url) window.location.href = url;
    },
  });
}

/** Open the Stripe Billing Portal and redirect to it. */
export function useOpenPortal() {
  return useMutation({
    mutationFn: () => billingService.createPortalSession(),
    onSuccess: (url) => {
      if (url) window.location.href = url;
    },
  });
}

export type { BillingStatus, PlanInterval, PlanTier };
